import { useApolloClient } from '@apollo/client';
import { useCallback, useEffect } from 'react';
import { getRawSessionStorageItem } from 'src/services/storage/session';
import { ERROR_PREFIXES, ROUTES } from 'src/common/constants';

import { getResolvedPath } from 'src/utils/getters/getResolvedPath';
import { useZillowSession } from '../../hooks/useZillowSession';
import { handleInvoluntarySessionTermination } from '../../session/handleInvoluntarySessionTermination';
import { handleSessionStart } from '../../session/handleSessionStart';

export const MAC_LOAN_ID_KEY = 'MortgageApplicationCenterSelectedLoan';
export const REFRESH_ERROR_MESSAGE = 'There was an issue refreshing your session. The page will now reload.';
export class SessionRefreshFailureError extends Error {
    type = 'SessionRefreshFailureError';
    constructor(errorMessage: string) {
        const prefixedMessage = `${ERROR_PREFIXES.APPLICATION_SESSION} ${errorMessage}`;
        super(prefixedMessage);
        this.type = 'SessionRefreshFailureError';
        this.name = 'SessionRefreshFailureError';
        this.message = prefixedMessage;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (this as any).__proto__ = SessionRefreshFailureError.prototype;
    }
}

export const useSessionWatcherState = () => {
    const apollo = useApolloClient();
    const { session } = useZillowSession();

    const onLogout = useCallback(async () => {
        await apollo.clearStore();
    }, [apollo]);

    const onSignInClick = useCallback(() => {
        const loanID = getRawSessionStorageItem(MAC_LOAN_ID_KEY);
        if (loanID) {
            globalThis.location.href = getResolvedPath(`${ROUTES.DASHBOARD}?loanID=${loanID}`);
            return;
        }
        handleSessionStart('Log back in banner clicked', {
            callbackUrl: getResolvedPath(ROUTES.DASHBOARD_BOUNCE),
        });
    }, []);

    useEffect(() => {
        const guardAgainstRefreshFailures = async () => {
            if (session?.error) {
                const { datadog } = await import(
                    /* webpackChunkName: "datadog" */
                    'src/services/datadog'
                );
                datadog.addError(new SessionRefreshFailureError('Session refresh failed'), {
                    error: session.error,
                });
                handleInvoluntarySessionTermination(REFRESH_ERROR_MESSAGE, 'Refresh failure', {
                    callbackUrl: window.location.pathname + window.location.search,
                });
            }
        };
        guardAgainstRefreshFailures();
    }, [session]);

    return { onLogout, onSignInClick, session };
};
